import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  api,
  type CasoDetalle,
  type CorreoOut,
} from "@/lib/api";
import { useAuth, canEdit } from "@/lib/auth";
import { Link } from "react-router-dom";
import AyudaVista from "@/ui/AyudaVista";
import ConfirmDialog from "@/ui/ConfirmDialog";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import { ayudaCorreosCaso } from "@/components/ayuda/vistas/correosCaso";
import EmailHtmlPreview from "@/components/EmailHtmlPreview";
import VisorArchivo from "@/ui/VisorArchivo";
import EnviarCorreoModal from "@/components/EnviarCorreoModal";
import { fmtBytes, mostrarMsgId } from "@/lib/formato";

type Props = {
  caso: CasoDetalle;
  onChanged?: () => void;
  // Si viene, al montar se abre/resalta ese correo (salto desde la bitácora).
  abrirCorreoId?: number | null;
};

const PREVIEW_LIGHT_KEY = "correos:preview-claro";

function minProgramableLocal(): string {
  // Datetime-local input no acepta tz: usamos hora local de la maquina del
  // user. Limite minimo "ya" + 5 min para evitar mandar "en el pasado" a
  // segundos de hacer click (el backend tambien valida >now).
  const d = new Date(Date.now() + 5 * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

type ResponderState = {
  destinatario: "cliente" | "jimi";
  toInicial: string;
  asuntoInicial: string;
  respondeACorreoId?: number;
  // Cita: id del correo cuyo cuerpo el backend anexa citado al final.
  citarCorreoId?: number;
  // Modo Reenviar (Fwd estilo Gmail): id del correo a reenviar tal cual.
  reenviarCorreoId?: number;
  comentarioInicial: string; // HTML — editor del mensaje al cliente
  // HTML para el editor del modo proveedor (ej. recordatorio + citado)
  comentarioProveedorInicial?: string;
  // "seguimiento" = correo corto (saludo + mensaje), sin resumen del caso
  plantillaInicial?: "completa" | "seguimiento";
};

export default function TabCorreos({ caso, onChanged, abrirCorreoId }: Props) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const [correos, setCorreos] = useState<CorreoOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviarA, setEnviarA] = useState<"cliente" | "jimi" | null>(null);
  const [responder, setResponder] = useState<ResponderState | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Imágenes del cuerpo, ya como datos, por correo. Se piden aquí —desde la
  // página, con sesión— porque el iframe donde se pinta el correo no tiene
  // origen y sus peticiones no llevan cookie: un <img src="/api/…"> de ahí
  // adentro se bloquea con ERR_BLOCKED_BY_ORB.
  const [imgsCorreo, setImgsCorreo] = useState<Record<number, Record<string, string>>>({});
  // Correo a resaltar al saltar desde la bitácora (evento "correo enviado").
  const [highlightId, setHighlightId] = useState<number | null>(null);

  /**
   * Las imágenes se piden porque el correo está ABIERTO, no porque alguien le
   * haya dado clic.
   *
   * Antes colgaban del `toggleExpand`, y hay otra puerta: al llegar desde la
   * bitácora («correo enviado») el correo se abre solo, sin pasar por ahí.
   * Resultado: la petición nunca salía, `imagenesCuerpo` se quedaba en
   * `undefined` y el panel decía **«Cargando el correo…» para siempre** —
   * exactamente lo que reportó Joe. Atar el efecto al estado, y no al gesto,
   * cierra las dos puertas y cualquiera que se agregue después.
   */
  const imgsPedidas = useRef<Set<number>>(new Set());
  useEffect(() => {
    expanded.forEach((id) => {
      if (imgsCorreo[id] !== undefined || imgsPedidas.current.has(id)) return;
      imgsPedidas.current.add(id);
      void api
        .get<Record<string, string>>(`/api/correos/${id}/imagenes`)
        .then((m) => setImgsCorreo((prev) => ({ ...prev, [id]: m })))
        // Un fallo no puede dejar el panel colgado: se pinta sin imágenes.
        .catch(() => setImgsCorreo((prev) => ({ ...prev, [id]: {} })));
    });
  }, [expanded, imgsCorreo]);

  // Al llegar desde el evento "correo enviado": abre, resalta y scrollea al correo.
  useEffect(() => {
    if (abrirCorreoId == null) return;
    if (!correos.some((c) => c.id === abrirCorreoId)) return;
    setExpanded((prev) => new Set(prev).add(abrirCorreoId));
    setHighlightId(abrirCorreoId);
    const tScroll = setTimeout(() => {
      document
        .getElementById(`correo-${abrirCorreoId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const tHi = setTimeout(() => setHighlightId(null), 2600);
    return () => {
      clearTimeout(tScroll);
      clearTimeout(tHi);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrirCorreoId, correos.length]);
  const [confirmDescartar, setConfirmDescartar] = useState<CorreoOut | null>(null);
  const [confirmDesasignar, setConfirmDesasignar] = useState<CorreoOut | null>(null);
  // Modal de reprogramar: el correo programado a editar + el datetime
  // string del input local (formato `YYYY-MM-DDTHH:mm`).
  const [reprogramarTarget, setReprogramarTarget] = useState<CorreoOut | null>(null);
  const [reprogramarFecha, setReprogramarFecha] = useState<string>("");
  // Bandera mientras corre /enviar-ahora — desactiva los botones del row
  // para evitar doble click que dispare otro send.
  const [enviandoAhoraId, setEnviandoAhoraId] = useState<number | null>(null);
  const [previewLight, setPreviewLight] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PREVIEW_LIGHT_KEY) === "1";
    } catch {
      return false;
    }
  });

  function togglePreviewLight() {
    setPreviewLight((v) => {
      const nv = !v;
      try {
        localStorage.setItem(PREVIEW_LIGHT_KEY, nv ? "1" : "0");
      } catch {
        /* ignore storage errors */
      }
      return nv;
    });
  }

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const list = await api.get<CorreoOut[]>(`/api/casos/${caso.id}/correos`);
      setCorreos(list);
    } catch (err) {
      // En el refresh silencioso no molestamos con un toast por un fallo
      // transitorio de red — se reintenta en el proximo tick.
      if (!quiet) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar correos", msg);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    setExpanded(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  // Auto-refresh: cada 30s recarga los correos del caso en SILENCIO para
  // mostrar correos entrantes nuevos sin refrescar la pagina. Local — solo
  // actualiza esta lista; no recarga el caso, no toca la bitacora, ni
  // colapsa los correos que tengas expandidos. Se pausa con la pestana en
  // segundo plano y recarga al volver.
  useEffect(() => {
    function tick() {
      if (!document.hidden) void load(true);
    }
    const id = setInterval(tick, 30_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  function handleResponder(c: CorreoOut) {
    // IN del cliente -> responder al cliente con quoted body en el editor
    const asuntoBase = c.asunto.replace(/^(Re|RE|Fwd|FWD):\s*/i, "");
    setResponder({
      destinatario: "cliente",
      toInicial: c.de,
      asuntoInicial: `Re: ${asuntoBase}`,
      comentarioInicial: "",
      // Hilo: el enviado se anida en la conversación del cliente.
      respondeACorreoId: c.id,
      // Cita: el backend anexa el cuerpo del correo original al final.
      citarCorreoId: c.id,
    });
  }

  function handleSeguimientoProveedor(c: CorreoOut) {
    // OUT enviado → recordatorio cortés EN EL MISMO HILO (In-Reply-To del
    // original), precargado al destinatario original. El humano revisa y
    // envía — nada sale solo.
    const asuntoBase = c.asunto.replace(/^(Re|RE|Fwd|FWD):\s*/i, "");
    setResponder({
      destinatario: "jimi",
      toInicial: c.para?.[0] ?? "",
      asuntoInicial: `Re: ${asuntoBase}`,
      comentarioInicial: "",
      // La plantilla de seguimiento ya saluda ("Hola {contacto},");
      // el cuerpo entra directo al grano. NO se referencia el caso ni el TE
      // en el texto (el proveedor identifica el caso por el asunto del hilo).
      comentarioProveedorInicial:
        `<p>Les escribimos para dar seguimiento a este caso, del cual ` +
        `seguimos en espera de su respuesta. Agradecemos mucho su apoyo ` +
        `para retomarlo.</p><p>Quedamos atentos.</p>`,
      // Sin plantillaInicial: usa el formato de PROVEEDOR completo
      // (render_correo_jimi), no el corto de seguimiento/cliente.
      // Hilo: encadena el recordatorio al mismo correo del proveedor.
      respondeACorreoId: c.id,
      // Cita: el backend anexa el cuerpo del correo anterior al final.
      citarCorreoId: c.id,
    });
  }

  function handleReenviar(c: CorreoOut) {
    // Reenviar correo (IN o OUT) estilo Gmail: el backend manda el ORIGINAL
    // tal cual (sin plantilla branded). El destino lo llena el user.
    const asuntoBase = c.asunto.replace(/^(Re|RE|Fwd|FWD):\s*/i, "");
    setResponder({
      destinatario: "cliente",
      toInicial: "",
      asuntoInicial: `Fwd: ${asuntoBase}`,
      comentarioInicial: "",
      // Modo reenvío: NO es reply-con-cita; un Fwd no encadena hilo.
      reenviarCorreoId: c.id,
    });
  }

  async function cancelarProgramado(c: CorreoOut) {
    try {
      await api.post(
        `/api/casos/${caso.id}/correos/${c.id}/cancelar-programado`,
      );
      toast.success("Envío programado cancelado");
      void load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cancelar", msg);
    }
  }

  function abrirReprogramar(c: CorreoOut) {
    setReprogramarTarget(c);
    // Pre-poblar con la fecha actual del scheduled (formato local sin tz
    // para que el datetime-local lo entienda directo).
    if (c.scheduled_at) {
      const d = new Date(c.scheduled_at);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setReprogramarFecha(local);
    } else {
      setReprogramarFecha("");
    }
  }

  async function confirmarReprogramar() {
    if (!reprogramarTarget || !reprogramarFecha) return;
    const iso = new Date(reprogramarFecha).toISOString();
    try {
      await api.patch(
        `/api/casos/${caso.id}/correos/${reprogramarTarget.id}/reprogramar`,
        { scheduled_at: iso },
      );
      toast.success(
        "Envío reprogramado",
        new Date(iso).toLocaleString(),
      );
      setReprogramarTarget(null);
      setReprogramarFecha("");
      void load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude reprogramar", msg);
    }
  }

  async function enviarAhora(c: CorreoOut) {
    setEnviandoAhoraId(c.id);
    try {
      await api.post(
        `/api/casos/${caso.id}/correos/${c.id}/enviar-ahora`,
      );
      toast.success("Correo enviado", c.asunto.slice(0, 60));
      void load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude enviar ahora", msg);
    } finally {
      setEnviandoAhoraId(null);
    }
  }

  async function handleDesasignar() {
    if (!confirmDesasignar) return;
    try {
      await api.post(`/api/correos/${confirmDesasignar.id}/desasignar`);
      toast.success("Correo regresado a bandeja");
      setConfirmDesasignar(null);
      void load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude desasignar", msg);
    }
  }

  async function handleDescartar() {
    if (!confirmDescartar) return;
    try {
      await api.del(`/api/correos/${confirmDescartar.id}`);
      toast.success("Correo descartado");
      setConfirmDescartar(null);
      void load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude descartar", msg);
    }
  }

  const sinSmtp = !me?.has_smtp_configured;
  const isAdmin = me?.rol === "admin";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-zinc-300">
            Correos ({correos.length})
          </h2>
          <AyudaVista
            titulo="Correos"
            {...ayudaCorreosCaso({ puedeEditar: canEdit(me), cerrado: caso.estado === "Cerrado", esAdmin: isAdmin })}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={togglePreviewLight} className="btn-ghost text-xs w-full sm:w-auto">
            Fondo: {previewLight ? "Claro" : "Oscuro"}
          </button>
          {canEdit(me) && caso.estado !== "Cerrado" && (
            <>
              <button
                onClick={() => setEnviarA("cliente")}
                className="btn-primary text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sinSmtp}
              >
                + Enviar al cliente
              </button>
              <button
                onClick={() => setEnviarA("jimi")}
                className="btn bg-sky-600 hover:bg-sky-500 text-white text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sinSmtp}
              >
                + Enviar a proveedor
              </button>
            </>
          )}
          {canEdit(me) && caso.estado === "Cerrado" && (
            <span className="text-xs text-zinc-500">
              Caso cerrado — no se pueden enviar correos nuevos.
            </span>
          )}
          {/* Por qué salen grises enviar/responder/reenviar: antes solo lo decía
              un tooltip, que en el celular no existe. */}
          {canEdit(me) && sinSmtp && (
            <span className="w-full text-[11px] text-amber-300 sm:w-auto">
              Para enviar, responder o reenviar, configura tu correo empresarial en{" "}
              <Link to="/perfil" className="underline hover:text-amber-200">Perfil</Link>.
            </span>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando…</p>
        ) : correos.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Sin correos todavía. Click en los botones de arriba para enviar.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {correos.map((c) => (
              <CorreoRow
                key={c.id}
                correo={c}
                isExpanded={expanded.has(c.id)}
                highlight={highlightId === c.id}
                onToggle={() => toggleExpand(c.id)}
                previewLight={previewLight}
                imagenesCuerpo={imgsCorreo[c.id]}
                canRespond={canEdit(me) && !sinSmtp}
                canEdit={canEdit(me)}
                isAdmin={isAdmin}
                onResponder={() => handleResponder(c)}
                onReenviar={() => handleReenviar(c)}
                onSeguimientoProveedor={() => handleSeguimientoProveedor(c)}
                onDesasignar={() => setConfirmDesasignar(c)}
                onDescartar={() => setConfirmDescartar(c)}
                onCancelarProgramado={() => cancelarProgramado(c)}
                onReprogramar={() => abrirReprogramar(c)}
                onEnviarAhora={() => enviarAhora(c)}
                enviandoAhora={enviandoAhoraId === c.id}
              />
            ))}
          </ul>
        )}
      </div>

      {enviarA && (
        <EnviarCorreoModal
          caso={caso}
          destinatario={enviarA}
          onClose={() => setEnviarA(null)}
          onSent={() => {
            void load();
            onChanged?.();
          }}
        />
      )}

      {responder && (
        <EnviarCorreoModal
          caso={caso}
          destinatario={responder.destinatario}
          toInicial={responder.toInicial}
          asuntoInicial={responder.asuntoInicial}
          comentarioInicial={responder.comentarioInicial}
          comentarioProveedorInicial={responder.comentarioProveedorInicial}
          plantillaInicial={responder.plantillaInicial}
          respondeACorreoId={responder.respondeACorreoId}
          citarCorreoId={responder.citarCorreoId}
          reenviarCorreoId={responder.reenviarCorreoId}
          onClose={() => setResponder(null)}
          onSent={() => {
            void load();
            onChanged?.();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDesasignar !== null}
        onOpenChange={(o) => !o && setConfirmDesasignar(null)}
        title="Regresar correo a bandeja"
        description={
          confirmDesasignar
            ? `Quitar "${confirmDesasignar.asunto.slice(0, 60)}" de este caso. Va a aparecer en /bandeja para reasignar a otro caso o descartar. Útil si la coincidencia del TE fue errónea.`
            : ""
        }
        confirmLabel="Regresar a bandeja"
        cancelLabel="Cancelar"
        onConfirm={handleDesasignar}
      />

      <ConfirmDialog
        open={confirmDescartar !== null}
        onOpenChange={(o) => !o && setConfirmDescartar(null)}
        title="Descartar correo"
        description={
          confirmDescartar
            ? `El correo "${confirmDescartar.asunto.slice(0, 60)}" se ocultará permanentemente. Queda registrado para auditoría. Solo admin puede hacer esto.`
            : ""
        }
        confirmLabel="Descartar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDescartar}
      />

      {reprogramarTarget && (
        <Modal
          open={true}
          onOpenChange={(o) => !o && setReprogramarTarget(null)}
          title="Reprogramar envío"
          description={`Cambiar la fecha/hora de "${reprogramarTarget.asunto.slice(0, 70)}"`}
          size="sm"
        >
          <div className="space-y-3">
            {reprogramarTarget.scheduled_at && (
              <p className="text-xs text-zinc-400">
                Actualmente programado para{" "}
                <span className="text-zinc-200">
                  {new Date(reprogramarTarget.scheduled_at).toLocaleString()}
                </span>
              </p>
            )}
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                Nueva fecha y hora
              </label>
              <input
                type="datetime-local"
                className="input w-full"
                value={reprogramarFecha}
                min={minProgramableLocal()}
                onChange={(e) => setReprogramarFecha(e.target.value)}
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                Hora local de tu equipo. El sistema manda el correo cuando
                llega esa fecha (resolución de 1 min).
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setReprogramarTarget(null)}
                className="btn-ghost text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmarReprogramar()}
                disabled={
                  !reprogramarFecha ||
                  new Date(reprogramarFecha).getTime() <= Date.now()
                }
                className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Guardar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// Item de correo: collapsed row + expand inline (cuerpo + acciones)
// ============================================================================

function CorreoRow({
  correo,
  isExpanded,
  highlight,
  onToggle,
  previewLight,
  imagenesCuerpo,
  canRespond,
  canEdit,
  isAdmin,
  onResponder,
  onReenviar,
  onSeguimientoProveedor,
  onDesasignar,
  onDescartar,
  onCancelarProgramado,
  onReprogramar,
  onEnviarAhora,
  enviandoAhora,
}: {
  correo: CorreoOut;
  isExpanded: boolean;
  highlight?: boolean;
  onToggle: () => void;
  previewLight: boolean;
  /** `{src original: data:…}` de las imágenes incrustadas; ver EmailHtmlPreview. */
  imagenesCuerpo?: Record<string, string>;
  canRespond: boolean;
  canEdit: boolean;
  isAdmin: boolean;
  onResponder: () => void;
  onReenviar: () => void;
  onSeguimientoProveedor: () => void;
  onDesasignar: () => void;
  onDescartar: () => void;
  onCancelarProgramado: () => void;
  onReprogramar: () => void;
  onEnviarAhora: () => void;
  enviandoAhora: boolean;
}) {
  const isOut = correo.direccion === "out";
  const hasHtml = !!correo.cuerpo_html?.trim();
  const [showHtml, setShowHtml] = useState(hasHtml);
  // Adjunto abierto en el visor (solo ver/descargar).
  const [verAdjunto, setVerAdjunto] = useState<CorreoOut["adjuntos"][number] | null>(
    null,
  );

  const recipientsLine = useMemo(() => {
    const parts: string[] = [];
    if (correo.para.length > 0) parts.push(`Para: ${correo.para.join(", ")}`);
    if (correo.cc.length > 0) parts.push(`Cc: ${correo.cc.join(", ")}`);
    return parts.join(" · ");
  }, [correo]);

  return (
    <li
      id={`correo-${correo.id}`}
      className={`py-3 ${highlight ? "rounded ring-2 ring-sky-400/60 -mx-1 px-1 transition" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left hover:bg-ink-800/50 -mx-2 px-2 py-1 rounded"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                  correo.es_interno
                    ? "border-violet-500/30 bg-violet-500/10 text-violet-300"
                    : isOut
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      : "border-sky-500/30 bg-sky-500/10 text-sky-300"
                }`}
              >
                {correo.es_interno ? "🔔 Interno" : isOut ? "↗ OUT" : "↘ IN"}
              </span>
              {correo.error && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-300">
                  ERROR
                </span>
              )}
              {correo.sent_at && (
                <span className="text-[10px] text-zinc-500">
                  {isOut ? "enviado" : "recibido"} {new Date(correo.sent_at).toLocaleString()}
                </span>
              )}
              {correo.scheduled_at && !correo.sent_at && (
                <span className="text-[10px] text-amber-300">
                  programado {new Date(correo.scheduled_at).toLocaleString()}
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-200 mt-1 truncate">{correo.asunto}</p>
            <p className="text-xs text-zinc-500 truncate">
              {isOut ? `→ ${correo.para.join(", ")}` : `de ${correo.de}`}
              {correo.cc.length > 0 && ` · cc ${correo.cc.join(", ")}`}
            </p>
            {correo.error && (
              <p className="text-xs text-red-300 mt-1">⚠ {correo.error}</p>
            )}
          </div>
          <div className="text-xs text-zinc-500 space-y-0.5 sm:text-right sm:flex-shrink-0">
            <div>{correo.enviado_por_nombre || (isOut ? "—" : correo.de)}</div>
            <div>{new Date(correo.ts).toLocaleString()}</div>
            <div className="text-accent-400 mt-1">
              {isExpanded ? "▲ ocultar" : "▼ ver"}
            </div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-2 ml-2 border-l-2 border-ink-700 pl-3 space-y-2 py-2">
          {recipientsLine && (
            <p className="text-xs text-zinc-500">{recipientsLine}</p>
          )}

          {hasHtml ? (
            <div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                <button
                  onClick={() => setShowHtml(true)}
                  className={`px-2 py-0.5 rounded ${showHtml ? "bg-ink-700 text-zinc-200" : "hover:bg-ink-800"}`}
                >
                  HTML
                </button>
                <button
                  onClick={() => setShowHtml(false)}
                  className={`px-2 py-0.5 rounded ${!showHtml ? "bg-ink-700 text-zinc-200" : "hover:bg-ink-800"}`}
                >
                  Texto
                </button>
              </div>
              {showHtml && imagenesCuerpo === undefined ? (
                /* Se espera al mapa de imágenes antes de pintar. Sin esto el
                   iframe se dibuja primero con las URLs, esas peticiones se
                   bloquean (no llevan cookie desde un iframe sin origen) y se
                   ve un parpadeo de imágenes rotas antes de que llegue el mapa. */
                <p className="rounded bg-ink-950 p-3 text-xs text-zinc-500">Cargando el correo…</p>
              ) : showHtml ? (
                // iframe sandbox — aisla los estilos del correo del SPA.
                // Los emails forwarded de Outlook traen `<html><style>...`
                // completo y eso pisa el tema del SPA si se inyecta con
                // dangerouslySetInnerHTML directo. Tambien limpia los
                // `cid:` images (attachments embebidos) por placeholder.
                <EmailHtmlPreview
                  html={correo.cuerpo_html || ""}
                  light={previewLight}
                  srcMap={imagenesCuerpo}
                  /* Antes 24rem fijos: el correo se veía chiquito y pegado
                     arriba con media pantalla libre debajo. Ahora usa el alto
                     de la ventana, con un piso para que no se aplaste en
                     pantallas bajas. */
                  maxHeight="min(70vh, 60rem)"
                />
              ) : (
                <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-ink-950 rounded p-3 max-h-96 overflow-auto">
                  {correo.cuerpo_texto?.trim() || "(sin cuerpo de texto)"}
                </pre>
              )}
            </div>
          ) : (
            <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-ink-950 rounded p-3 max-h-96 overflow-auto">
              {correo.cuerpo_texto?.trim() || "(sin cuerpo)"}
            </pre>
          )}

          {/* Adjuntos del correo (solo ver o descargar) */}
          {correo.adjuntos?.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {correo.adjuntos.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setVerAdjunto(a)}
                  className="text-[11px] px-2 py-0.5 rounded bg-ink-800 border border-ink-600 text-zinc-300 hover:border-accent-500 text-left break-words [overflow-wrap:anywhere]"
                >
                  📎 {a.nombre} · {fmtBytes(a.bytes)}
                </button>
              ))}
            </div>
          )}
          {verAdjunto && (
            <VisorArchivo
              targets={correo.adjuntos.map((x) => ({
                nombre: x.nombre,
                mime: x.mime,
                url: `/api/correos/adjuntos/${x.id}/file`,
                previewUrl: `/api/correos/adjuntos/${x.id}/preview`,
              }))}
              inicial={Math.max(
                0,
                correo.adjuntos.findIndex((x) => x.id === verAdjunto.id),
              )}
              onClose={() => setVerAdjunto(null)}
            />
          )}

          {/* Acciones segun direccion */}
          <div className="flex items-center gap-2 pt-2 border-t border-ink-700 flex-wrap">
            {isOut && correo.scheduled_at && !correo.sent_at && canEdit && (
              <>
                <button
                  onClick={onEnviarAhora}
                  disabled={enviandoAhora}
                  className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {enviandoAhora ? "Enviando…" : "↗ Enviar ahora"}
                </button>
                <button
                  onClick={onReprogramar}
                  disabled={enviandoAhora}
                  className="btn-ghost text-sm text-sky-400 hover:text-sky-300 disabled:opacity-50"
                >
                  🕒 Reprogramar
                </button>
                <button
                  onClick={onCancelarProgramado}
                  disabled={enviandoAhora}
                  className="btn-ghost text-sm text-amber-400 hover:text-amber-300 disabled:opacity-50"
                >
                  Cancelar envío
                </button>
              </>
            )}
            {!isOut && (
              <button
                onClick={onResponder}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canRespond}
              >
                ↩ Responder
              </button>
            )}
            {isOut && correo.sent_at && !correo.es_interno && (
              <button
                onClick={onSeguimientoProveedor}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canRespond}
              >
                ⏰ Recordar al proveedor
              </button>
            )}
            <button
              onClick={onReenviar}
              className="btn-ghost text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canRespond}
            >
              ↪ Reenviar
            </button>
            {!isOut && canEdit && (
              <button
                onClick={onDesasignar}
                className="btn-ghost text-sm text-amber-400 hover:text-amber-300"
              >
                ↺ Regresar a bandeja
              </button>
            )}
            {isAdmin && (
              <button
                onClick={onDescartar}
                className="btn-ghost text-sm text-red-400 hover:text-red-300"
              >
                🗑 Descartar
              </button>
            )}
            <span className="ml-auto min-w-0 max-w-full break-all text-[10px] text-zinc-600">
              ID #{correo.id}
              {correo.message_id && <> · {mostrarMsgId(correo.message_id).slice(0, 40)}…</>}
            </span>
          </div>
        </div>
      )}
    </li>
  );
}
